// UserMenu — header dropdown shown to authenticated users.
//
// Responsibilities:
//   - Render the authenticated user's identity (initial + email) and a
//     small popover with two canonical actions: "Account" (link) and
//     "Sign out" (button). Account lives only here; the primary nav in
//     the header does not repeat it.
//   - Accept an optional `mobileSlot` so callers (the AppHeader) can stuff
//     primary nav links into the menu on narrow viewports without having
//     to roll a separate hamburger control. The slot is rendered behind
//     a `sm:hidden` wrapper inside the menu, so on >=640px the menu
//     contains only Account + Sign out.
//   - Be keyboard-accessible:
//       * trigger has aria-haspopup/aria-expanded/aria-label
//       * Escape closes the menu and returns focus to the trigger
//       * outside click closes the menu
//       * any link/menuitem click inside closes the menu (delegated)
//   - Stay decoupled from the AppHeader's nav config: the menu doesn't
//     know which routes exist; the slot owner does.
//
// Sign-out flow (shared via `useSignOut`):
//   Mirrors `apps/web/src/routes/account.tsx` exactly so the two surfaces
//   stay aligned and a single change site covers both. We:
//     1. Clear the persisted auth store (token + user).
//     2. Clear the entire react-query cache so a subsequent sign-in by
//        a different user cannot see stale data flash through.
//     3. Navigate to /login with `replace: true` so the back button
//        doesn't trap the user behind a now-logged-out screen.
//   `api-client.ts` already calls `useAuthStore.getState().clear()` on
//   401s, so the same clear() call is idempotent if the 401 handler runs
//   first — set-to-null is safe to repeat.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, LogOut, User as UserIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuthStore, useAuthUser } from '@/stores/auth-store'
import { revokeAuthSession } from '@/lib/api-client'
import { cn } from '@/lib/utils'

/**
 * Shared sign-out hook. Exported so the account page (and any future
 * sign-out affordance) can call the exact same flow without duplicating
 * navigate/queryClient wiring.
 */
export function useSignOut(): () => void {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  return useCallback(() => {
    // Order matters: clear store first so any subscribed UI immediately
    // flips to logged-out, then drop query cache before the new
    // navigation triggers fresh fetches.
    const auth = useAuthStore.getState()
    const refreshToken = auth.refreshToken
    auth.clear()
    queryClient.clear()
    revokeAuthSession(refreshToken)
    void navigate({ to: '/login', replace: true })
  }, [navigate, queryClient])
}

export interface UserMenuProps {
  /**
   * Optional content rendered inside the popover ABOVE the standard
   * Account/Sign-out items. Intended for mobile-only nav collapse — the
   * caller should wrap it in a `sm:hidden` div so it disappears on >=640px.
   */
  mobileSlot?: ReactNode
  /** Extra classes applied to the outer wrapper (sets the popover anchor). */
  className?: string
}

export function UserMenu({ mobileSlot, className }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const user = useAuthUser()
  const signOut = useSignOut()

  // Wire Escape + outside-click while the menu is open. Both listeners
  // are torn down on close so we don't pay event-loop cost when idle.
  useEffect(() => {
    if (!open) return undefined
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const handlePointer = (event: MouseEvent): void => {
      const target = event.target as Node | null
      if (target && !containerRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handleKey)
    // mousedown (not click) so we close BEFORE focus moves into the
    // newly-clicked element, matching native menu behavior.
    window.addEventListener('mousedown', handlePointer)
    return () => {
      window.removeEventListener('keydown', handleKey)
      window.removeEventListener('mousedown', handlePointer)
    }
  }, [open])

  // Delegated close: any anchor/menuitem click inside the popover closes
  // it. Saves wiring an explicit onClick on every link, including any
  // links the caller passes via mobileSlot.
  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const el = event.target as HTMLElement | null
    if (el?.closest('a, [role="menuitem"]')) {
      setOpen(false)
    }
  }

  const handleSignOut = (): void => {
    setOpen(false)
    signOut()
  }

  // Identity affordance — circle with first email letter. Falls back to
  // 'U' if the user has somehow rendered before the store hydrated.
  const initial = user?.email?.[0]?.toUpperCase() ?? 'U'
  const label = user?.email ?? 'Account'

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="user-menu-popover"
        aria-label={`Open account menu for ${label}`}
        onClick={() => setOpen((v) => !v)}
        className="gap-2 px-2"
        data-testid="user-menu-trigger"
      >
        <span
          aria-hidden="true"
          className="flex h-6 w-6 items-center justify-center rounded-full border border-border bg-muted text-xs font-semibold text-foreground"
        >
          {initial}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'opacity-70 transition-transform',
            open && 'rotate-180',
          )}
        />
      </Button>
      {open ? (
        <div
          id="user-menu-popover"
          role="menu"
          aria-label="Account menu"
          data-testid="user-menu"
          onClick={handleMenuClick}
          className={cn(
            'absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden',
            'rounded-xl border border-border bg-popover p-1',
            'text-popover-foreground shadow-md',
          )}
        >
          {user?.email ? (
            <div
              className="border-b border-border px-3 py-2 text-xs text-muted-foreground"
              // Long emails wrap rather than horizontally overflow at 360px.
              style={{ wordBreak: 'break-word' }}
            >
              {user.email}
            </div>
          ) : null}
          {mobileSlot ? (
            <div className="border-b border-border py-1">{mobileSlot}</div>
          ) : null}
          <div className="py-1">
            <Link
              to="/account"
              role="menuitem"
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
              data-testid="user-menu-account"
            >
              <UserIcon aria-hidden="true" className="size-4 shrink-0" />
              Account
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
              data-testid="user-menu-signout"
            >
              <LogOut aria-hidden="true" className="size-4 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default UserMenu
